import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import SEOHead from '@/components/base/SEOHead';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <>
        <SEOHead title="漫画未找到" description="您要查找的漫画不存在。" />
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-950">漫画未找到</h1>
            <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((cat) => cat.slug === comic.category);
  const relatedComics = comics
    .filter((c) => c.id !== comic.id && (c.category === comic.category || c.tags.some((t) => comic.tags.includes(t))))
    .slice(0, 5);

  const mockChapters = Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => ({
    num: comic.chapters - i,
    title: `第${comic.chapters - i}话${i === 0 ? '（最新）' : ''}`,
    date: new Date(2026, 6, 7 - i).toISOString().split('T')[0],
  }));

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 爱漫画 韩国漫画在线阅读`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2025-01-01',
    dateModified: comic.updatedAt,
    image: comic.cover,
    publisher: {
      '@type': 'Organization',
      name: '爱漫画',
    },
  };

  return (
    <>
      <SEOHead
        title={comic.title}
        description={`${comic.title} - ${comic.description.slice(0, 120)}`}
        keywords={`${comic.title},韩国漫画,${comic.tags.join(',')},爱漫画,韩漫在线阅读`}
        canonicalUrl={`https://aimanga.com/comic/${comic.slug}`}
        ogType="article"
        schema={articleSchema}
      />
      <Navbar />
      <main>
        <section className="w-full pt-20 pb-12 md:pb-16 bg-background-50">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: category?.name || '全部分类', path: category ? `/category/${category.slug}` : '/' },
              { label: comic.title },
            ]}
          />
          <div className="px-4 md:px-6 max-w-7xl mx-auto mt-6">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-12">
              <div className="w-full lg:w-72 shrink-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden border border-background-200/70">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 韩国漫画 免费在线阅读`}
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                </div>
              </div>

              <div className="flex-1">
                <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold">
                  {comic.status}
                </span>
                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black text-foreground-950 mt-4 leading-tight">
                  {comic.title}
                </h1>
                <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-600">
                  <span>作者：<strong className="text-foreground-900">{comic.author}</strong></span>
                  <span className="hidden sm:inline">|</span>
                  <span>分类：
                    <Link
                      to={`/category/${comic.category}`}
                      className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
                    >
                      {category?.name}
                    </Link>
                  </span>
                  <span className="hidden sm:inline">|</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-400"></i>
                    <strong className="text-foreground-900">{comic.rating}</strong> 分
                  </span>
                  <span className="hidden sm:inline">|</span>
                  <span>共 <strong className="text-foreground-900">{comic.chapters}</strong> 话</span>
                  <span className="hidden sm:inline">|</span>
                  <span><strong className="text-foreground-900">{(comic.views / 10000).toFixed(0)}万</strong> 次阅读</span>
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mt-6 bg-background-100 rounded-lg p-5 md:p-6">
                  <h2 className="font-heading text-lg font-bold text-foreground-950 mb-2">作品简介</h2>
                  <p className="text-foreground-700 text-sm md:text-base leading-relaxed">
                    {comic.description}
                  </p>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <button className="whitespace-nowrap px-8 py-3 rounded-full bg-primary-500 text-background-50 text-base font-semibold hover:bg-primary-600 transition-all cursor-pointer flex items-center gap-2">
                    <i className="ri-play-circle-line"></i> 开始阅读
                  </button>
                  <button className="whitespace-nowrap px-8 py-3 rounded-full border-2 border-foreground-200 text-foreground-700 text-base font-medium hover:border-primary-300 hover:text-primary-600 transition-all cursor-pointer flex items-center gap-2">
                    <i className="ri-bookmark-line"></i> 加入收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-16 bg-background-100">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8">
              章节列表
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {mockChapters.map((ch) => (
                <a
                  key={ch.num}
                  href="#"
                  className="flex items-center justify-between px-5 py-3.5 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/50 transition-all cursor-pointer"
                  onClick={(e) => e.preventDefault()}
                >
                  <span className="text-sm font-medium text-foreground-800">{ch.title}</span>
                  <span className="text-xs text-foreground-500">{ch.date}</span>
                </a>
              ))}
            </div>
            {comic.chapters > 20 && (
              <div className="mt-6 text-center">
                <button className="whitespace-nowrap px-6 py-2.5 rounded-full bg-background-50 border border-background-200 text-foreground-600 text-sm font-medium hover:border-primary-300 hover:text-primary-600 transition-all cursor-pointer">
                  展开更多章节（共{comic.chapters}话）
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="w-full py-12 md:py-16 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                相关推荐
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5" data-product-shop="">
              {relatedComics.map((rc) => (
                <ComicCard
                  key={rc.id}
                  slug={rc.slug}
                  title={rc.title}
                  cover={rc.cover}
                  status={rc.status}
                  rating={rc.rating}
                  chapters={rc.chapters}
                  tags={rc.tags}
                  isNew={rc.isNew}
                  isHot={rc.isHot}
                />
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}